import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Plus, Send, Trash2, ArrowLeft, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';

export default function ChatPage() {
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [activeConv, setActiveConv] = useState(null);
  const [showSidebar, setShowSidebar] = useState(true);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    loadConversations();
  }, []);

  useEffect(() => {
    if (conversationId) {
      loadMessages(conversationId);
      setShowSidebar(false);
    }
  }, [conversationId]);

  const loadConversations = async () => {
    try {
      const res = await api.get('/conversations');
      setConversations(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const loadMessages = async (convId) => {
    try {
      const res = await api.get(`/conversations/${convId}/messages`);
      setMessages(res.data);
      const conv = conversations.find(c => c.conversation_id === convId);
      setActiveConv(conv || { conversation_id: convId });
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch (err) {
      console.error(err);
    }
  };

  const createConversation = async () => {
    try {
      const res = await api.post('/conversations', { title: 'New Conversation' });
      setConversations(prev => [res.data, ...prev]);
      navigate(`/chat/${res.data.conversation_id}`);
    } catch (err) {
      toast.error('Could not start a new session');
    }
  };

  const deleteConversation = async (convId, e) => {
    e.stopPropagation();
    try {
      await api.delete(`/conversations/${convId}`);
      setConversations(prev => prev.filter(c => c.conversation_id !== convId));
      if (conversationId === convId) {
        navigate('/chat');
        setMessages([]);
        setActiveConv(null);
      }
      toast.success('Conversation removed');
    } catch (err) {
      toast.error('Could not delete');
    }
  };

  const sendMessage = async () => {
    if (!input.trim() || sending) return;
    if (!conversationId) {
      toast.error('Please start a new session first');
      return;
    }

    const text = input.trim();
    setInput('');
    setSending(true);

    // Optimistic UI
    const tempUserMsg = { message_id: 'temp_user', role: 'user', content: text, created_at: new Date().toISOString() };
    setMessages(prev => [...prev, tempUserMsg]);
    setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);

    try {
      const res = await api.post(`/conversations/${conversationId}/messages`, { content: text });
      setMessages(prev => {
        const filtered = prev.filter(m => m.message_id !== 'temp_user');
        return [...filtered, { ...tempUserMsg, message_id: 'sent_user_' + Date.now() }, res.data];
      });
      loadConversations();
    } catch (err) {
      toast.error('Could not send message. Please try again.');
      setMessages(prev => prev.filter(m => m.message_id !== 'temp_user'));
      setInput(text);
    } finally {
      setSending(false);
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div className="flex h-[calc(100vh-5rem)] lg:h-[calc(100vh-5rem)] -mx-6 lg:-mx-10 -mt-6 lg:-mt-10" data-testid="chat-page">
      {/* Conversation List */}
      <div className={`${showSidebar ? 'flex' : 'hidden'} lg:flex flex-col w-full lg:w-80 border-r border-black/8 bg-bone/50 shrink-0`}>
        <div className="p-5 border-b border-black/8">
          <div className="flex items-center justify-between mb-4 pr-9 lg:pr-0">
            <h2 className="font-serif text-2xl text-dark">Sessions</h2>
            <Button
              size="sm"
              onClick={createConversation}
              data-testid="new-conversation-button"
              className="bg-dark hover:bg-dark/90 text-bone rounded-xl font-sans text-xs tracking-wider btn-hover"
            >
              <Plus size={14} className="mr-1" /> New
            </Button>
          </div>
        </div>
        <ScrollArea className="flex-1 custom-scrollbar">
          <div className="p-3 space-y-1">
            {conversations.length === 0 ? (
              <div className="text-center py-12 px-4">
                <MessageCircle size={32} className="mx-auto text-text-muted/40 mb-3" />
                <p className="text-text-muted font-sans text-sm">Start your first coaching session</p>
              </div>
            ) : (
              conversations.map((conv) => (
                <button
                  key={conv.conversation_id}
                  onClick={() => navigate(`/chat/${conv.conversation_id}`)}
                  data-testid={`conv-item-${conv.conversation_id}`}
                  className={`w-full text-left px-4 py-3 rounded-xl group ${
                    conversationId === conv.conversation_id ? 'bg-gold/15' : 'hover:bg-sand/60'
                  }`}
                  style={{ transition: 'background-color 0.2s ease' }}
                >
                  <div className="flex items-center justify-between">
                    <p className="font-sans text-sm text-dark truncate pr-2">{conv.title}</p>
                    <button
                      onClick={(e) => deleteConversation(conv.conversation_id, e)}
                      data-testid={`delete-conv-${conv.conversation_id}`}
                      className="opacity-0 group-hover:opacity-100 text-text-muted hover:text-red-500 shrink-0"
                      style={{ transition: 'opacity 0.2s ease, color 0.2s ease' }}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                  <p className="text-text-muted font-sans text-xs mt-0.5">
                    {conv.message_count || 0} messages
                  </p>
                </button>
              ))
            )}
          </div>
        </ScrollArea>
      </div>

      {/* Chat Area */}
      <div className={`${!showSidebar || !conversationId ? 'flex' : 'hidden'} lg:flex flex-col flex-1 bg-sand/30`}>
        {!conversationId ? (
          <div className="flex-1 flex items-center justify-center p-8">
            <div className="text-center max-w-md animate-fade-up relative">
              {/* Sacred geometry background */}
              <div className="absolute inset-0 flex items-center justify-center opacity-[0.03] pointer-events-none">
                <svg viewBox="0 0 200 200" width="400" height="400">
                  <circle cx="100" cy="100" r="90" fill="none" stroke="#C6B28F" strokeWidth="0.5" />
                  <circle cx="100" cy="100" r="70" fill="none" stroke="#C6B28F" strokeWidth="0.3" />
                  <circle cx="100" cy="100" r="50" fill="none" stroke="#7A8274" strokeWidth="0.3" />
                  <circle cx="100" cy="100" r="30" fill="none" stroke="#C6B28F" strokeWidth="0.5" />
                  <polygon points="100,10 190,145 10,145" fill="none" stroke="#C6B28F" strokeWidth="0.3" />
                  <polygon points="100,190 10,55 190,55" fill="none" stroke="#C6B28F" strokeWidth="0.3" />
                </svg>
              </div>
              <div className="w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center animate-breathe relative">
                <div className="absolute inset-0 rounded-full bg-gold/15 animate-breathe" style={{ animationDelay: '0.5s' }} />
                <div className="absolute inset-2 rounded-full bg-gold/10 animate-breathe" style={{ animationDelay: '1s' }} />
                <MessageCircle size={28} className="text-gold relative z-10" />
              </div>
              <h2 className="font-serif text-3xl text-dark mb-3 mystical-heading">Begin a Coaching Session</h2>
              <p className="text-text-muted font-sans text-sm mb-6 leading-relaxed">
                Your AI coach is here to guide you in embodying the identity of your highest self. Every conversation builds on the last.
              </p>
              <div className="mystical-divider mb-6" />
              <Button
                onClick={createConversation}
                data-testid="start-session-button"
                className="bg-dark hover:bg-dark/90 text-bone rounded-xl font-sans text-sm tracking-wider btn-hover"
              >
                <Plus size={16} className="mr-2" /> Start New Session
              </Button>
            </div>
          </div>
        ) : (
          <>
            {/* Chat Header */}
            <div className="p-4 border-b border-black/8 flex items-center gap-3 bg-bone/40 backdrop-blur-sm">
              <button
                onClick={() => setShowSidebar(true)}
                className="lg:hidden text-text-muted hover:text-dark"
                data-testid="chat-back-button"
              >
                <ArrowLeft size={20} />
              </button>
              <div>
                <p className="font-serif text-lg text-dark">{activeConv?.title || 'Session'}</p>
                <p className="text-text-muted font-sans text-xs">{messages.length} messages</p>
              </div>
            </div>

            {/* Messages */}
            <ScrollArea className="flex-1 custom-scrollbar">
              <div className="p-6 lg:p-10 space-y-8 max-w-3xl mx-auto">
                {messages.length === 0 && (
                  <div className="text-center py-16 animate-fade-up relative">
                    <div className="absolute inset-0 flex items-center justify-center opacity-[0.025] pointer-events-none">
                      <svg viewBox="0 0 200 200" width="300" height="300">
                        <circle cx="100" cy="100" r="80" fill="none" stroke="#C6B28F" strokeWidth="0.4" />
                        <circle cx="100" cy="100" r="50" fill="none" stroke="#7A8274" strokeWidth="0.3" />
                        <polygon points="100,20 180,140 20,140" fill="none" stroke="#C6B28F" strokeWidth="0.3" />
                        <polygon points="100,180 20,60 180,60" fill="none" stroke="#C6B28F" strokeWidth="0.3" />
                      </svg>
                    </div>
                    <p className="font-serif text-2xl text-dark italic mb-2 mystical-heading">
                      "The invitation is always the same: come home to yourself."
                    </p>
                    <p className="text-text-muted font-sans text-sm">Share what's alive in you today.</p>
                    <div className="mystical-divider mt-6 max-w-xs mx-auto" />
                  </div>
                )}
                {messages.map((msg, i) => (
                  <div
                    key={msg.message_id || i}
                    className="animate-fade-up"
                    style={{ animationDelay: `${Math.min(i * 0.05, 0.3)}s` }}
                    data-testid={`chat-message-${msg.role}`}
                  >
                    {msg.role === 'assistant' ? (
                      <div className="chat-ai-message">
                        <p className="text-text-muted font-sans text-xs tracking-[0.2em] uppercase mb-3">Coach</p>
                        {msg.content.split('\n').filter(Boolean).map((para, j) => (
                          <p key={j} className="mb-3 last:mb-0">{para}</p>
                        ))}
                      </div>
                    ) : (
                      <div className="flex justify-end">
                        <div className="chat-user-message">
                          {msg.content}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
                {sending && (
                  <div className="animate-fade-up" data-testid="chat-loading">
                    <p className="text-text-muted font-sans text-xs tracking-[0.2em] uppercase mb-3">Coach</p>
                    <div className="flex gap-2 py-2">
                      <span className="loading-dot" />
                      <span className="loading-dot" />
                      <span className="loading-dot" />
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
            </ScrollArea>

            {/* Input */}
            <div className="p-4 lg:p-6 border-t border-black/8 bg-bone/40 backdrop-blur-sm">
              <div className="max-w-3xl mx-auto flex gap-3">
                <Textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Share what's on your mind..."
                  data-testid="chat-input"
                  className="flex-1 min-h-[48px] max-h-[120px] resize-none bg-bone/60 border-black/8 focus:border-gold focus:ring-gold/30 rounded-xl font-sans text-sm"
                  rows={1}
                />
                <Button
                  onClick={sendMessage}
                  disabled={!input.trim() || sending}
                  data-testid="chat-send-button"
                  className="h-12 w-12 bg-dark hover:bg-dark/90 text-bone rounded-xl shrink-0 btn-hover disabled:opacity-40"
                >
                  <Send size={18} />
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
